import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../../msalConfig';
import { listPhotos, uploadPhoto, updatePhotoMetadata, deletePhoto } from '../api/photoApi';
import { extractPhotoMetadata } from '../api/metadataExtraction';
import MediaCover from '../components/MediaCover';
import PhotoLightbox from '../components/PhotoLightbox';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// Runs fn(item, index) for each item with at most `limit` concurrent promises.
async function runConcurrently(arr, fn, limit) {
  const results = new Array(arr.length);
  let idx = 0;
  async function worker() {
    while (idx < arr.length) {
      const i = idx++;
      try { results[i] = await fn(arr[i], i); }
      catch (err) { results[i] = { error: err }; }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, arr.length) }, worker));
  return results;
}

function photoCardLabel(item) {
  if (item.Caption)                      return { text: item.Caption,         untitled: false };
  if (item.hasCustomTitle && item.Title) return { text: item.Title,           untitled: false };
  if (item.AlbumCollection)              return { text: item.AlbumCollection, untitled: false };
  if (item.People)                       return { text: item.People,          untitled: false };
  return { text: item.name || item.Title, untitled: true };
}

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${month} ${d.getUTCFullYear()}`;
}

// ── Upload Modal ──────────────────────────────────────────────────────────────

function UploadModal({ queue, applyToAll, phase, results, onClose, onMetaChange, onApplyAllChange, onApplyAll, onUploadAll }) {
  const doneCount = queue.filter(e => results[e.id]?.status === 'done' || results[e.id]?.status === 'error').length;
  const allDone   = queue.length > 0 && doneCount === queue.length;

  return (
    <div className="fv-upload-overlay" onClick={phase === 'form' ? onClose : undefined}>
      <div className="fv-upload-modal" onClick={e => e.stopPropagation()}>

        <div className="fv-upload-modal-header">
          <h2 className="fv-upload-modal-title">
            {phase === 'form' ? `Upload Photos (${queue.length})` : `Uploading… ${doneCount}/${queue.length}`}
          </h2>
          {phase === 'form' && (
            <button className="fv-upload-modal-close" onClick={onClose}>&#x2715;</button>
          )}
        </div>

        {phase === 'form' && (
          <div className="fv-upload-apply-all">
            <span className="fv-upload-apply-label">Apply to all:</span>
            <input className="fv-upload-apply-input" placeholder="Project" value={applyToAll.Project}
              onChange={e => onApplyAllChange('Project', e.target.value)} />
            <input className="fv-upload-apply-input" placeholder="People (additive)" value={applyToAll.People}
              onChange={e => onApplyAllChange('People', e.target.value)} />
            <input className="fv-upload-apply-input" placeholder="Tags (additive)" value={applyToAll.Tags}
              onChange={e => onApplyAllChange('Tags', e.target.value)} />
            <input className="fv-upload-apply-input" placeholder="Source" value={applyToAll.Source}
              onChange={e => onApplyAllChange('Source', e.target.value)} />
            <button className="fv-browse-action-btn" onClick={onApplyAll}>Apply</button>
          </div>
        )}

        <div className="fv-upload-list">
          {queue.map(entry => {
            const result = results[entry.id];
            return (
              <div key={entry.id} className="fv-upload-row">
                <div className="fv-upload-thumb-wrap">
                  {entry.preview
                    ? <img className="fv-upload-thumb" src={entry.preview} alt={entry.file.name} />
                    : <div className="fv-upload-thumb fv-upload-thumb--loading" />}
                </div>

                {phase === 'form' ? (
                  <div className="fv-upload-meta">
                    <UploadField label="Title" value={entry.meta.Title}
                      onChange={v => onMetaChange(entry.id, 'Title', v)} />
                    <div className="fv-upload-meta-row">
                      <label className="fv-upload-meta-label">Date</label>
                      <input className="fv-upload-meta-input" type="date" value={entry.meta.DateTaken}
                        onChange={e => onMetaChange(entry.id, 'DateTaken', e.target.value)} />
                      {!entry.exifLoaded && <span className="fv-upload-exif-hint">reading EXIF…</span>}
                    </div>
                    <UploadField label="People" placeholder="comma-separated" value={entry.meta.People}
                      onChange={v => onMetaChange(entry.id, 'People', v)} />
                    <UploadField label="Project" value={entry.meta.Project}
                      onChange={v => onMetaChange(entry.id, 'Project', v)} />
                    <UploadField label="Tags" placeholder="comma-separated" value={entry.meta.Tags}
                      onChange={v => onMetaChange(entry.id, 'Tags', v)} />
                    <UploadField label="Location" value={entry.meta.Location}
                      onChange={v => onMetaChange(entry.id, 'Location', v)} />
                    <UploadField label="Caption" value={entry.meta.Caption}
                      onChange={v => onMetaChange(entry.id, 'Caption', v)} />
                    <UploadField label="Source" value={entry.meta.Source}
                      onChange={v => onMetaChange(entry.id, 'Source', v)} />
                  </div>
                ) : (
                  <div className="fv-upload-progress-info">
                    <span className="fv-upload-progress-name">{entry.file.name}</span>
                    {!result                          && <span className="fv-upload-status fv-upload-status--pending">Pending</span>}
                    {result?.status === 'uploading'   && <span className="fv-upload-status fv-upload-status--uploading">Uploading…</span>}
                    {result?.status === 'done'        && <span className="fv-upload-status fv-upload-status--done">Done</span>}
                    {result?.status === 'error'       && (
                      <span className="fv-upload-status fv-upload-status--error" title={result.error}>Failed</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="fv-upload-modal-footer">
          {phase === 'form' && (
            <>
              <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={onClose}>Cancel</button>
              <button className="fv-browse-action-btn" onClick={onUploadAll}>
                Upload All ({queue.length})
              </button>
            </>
          )}
          {phase === 'progress' && !allDone && (
            <span className="fv-upload-progress-label">{doneCount} / {queue.length} complete</span>
          )}
          {phase === 'progress' && allDone && (
            <button className="fv-browse-action-btn" onClick={onClose}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadField({ label, value, onChange, placeholder }) {
  return (
    <div className="fv-upload-meta-row">
      <label className="fv-upload-meta-label">{label}</label>
      <input className="fv-upload-meta-input" value={value} placeholder={placeholder || ''}
        onChange={e => onChange(e.target.value)} />
    </div>
  );
}

// ── Bulk Action Modal ─────────────────────────────────────────────────────────

function BulkModal({ modal, count, running, onChange, onConfirm, onClose }) {
  const labels = {
    tag:    `Add tag to ${count} photo${count !== 1 ? 's' : ''}:`,
    album:  `Set Project for ${count} photo${count !== 1 ? 's' : ''}:`,
    people: `Add person to ${count} photo${count !== 1 ? 's' : ''}:`,
  };
  const placeholders = { tag: 'tag name', album: 'project name', people: 'person name' };

  return (
    <div className="fv-bulk-overlay" onClick={onClose}>
      <div className="fv-bulk-modal" onClick={e => e.stopPropagation()}>
        <p className="fv-bulk-modal-label">{labels[modal.type]}</p>
        <input
          className="fv-detail-input"
          value={modal.value}
          placeholder={placeholders[modal.type] || ''}
          onChange={e => onChange(e.target.value)}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') onConfirm(); if (e.key === 'Escape') onClose(); }}
        />
        <div className="fv-detail-edit-actions" style={{ marginTop: '0.75rem' }}>
          <button className="fv-browse-action-btn" onClick={onConfirm} disabled={running || !modal.value.trim()}>
            {running ? 'Applying…' : 'Apply'}
          </button>
          <button className="fv-browse-action-btn fv-browse-action-btn--cancel" onClick={onClose} disabled={running}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Photos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { instance, accounts } = useMsal();
  const fileInputRef = useRef(null);
  const lightboxHistoryRef = useRef(false);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  // Browse
  const [items,  setItems]  = useState(null);
  const [error,  setError]  = useState(null);
  const [sortBy, setSortBy] = useState('event');

  // Primary filters — read from URL (bookmarkable)
  const filterAlbum  = searchParams.get('album')  || 'all';
  const filterPerson = searchParams.get('person') || 'all';
  const filterTag    = searchParams.get('tag')    || 'all';

  // Secondary filters — read from URL
  const filterYear   = searchParams.get('year')   || 'all';
  const filterMonth  = searchParams.get('month')  || 'all';
  const filterSource = searchParams.get('source') || 'all';

  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Selection
  const [selectionMode,     setSelectionMode]     = useState(false);
  const [selectedIds,       setSelectedIds]       = useState(new Set());

  // Lightbox
  const [lightboxId, setLightboxId] = useState(null);

  // Upload
  const [uploadOpen,    setUploadOpen]    = useState(false);
  const [uploadQueue,   setUploadQueue]   = useState([]);
  const [applyToAll,    setApplyToAll]    = useState({ Project: '', People: '', Tags: '', Source: '' });
  const [uploadPhase,   setUploadPhase]   = useState('form');
  const [uploadResults, setUploadResults] = useState({});

  // Bulk
  const [bulkModal,         setBulkModal]         = useState(null);
  const [bulkRunning,       setBulkRunning]       = useState(false);
  const [bulkError,         setBulkError]         = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [downloadProgress,  setDownloadProgress]  = useState(null);

  // ── Load ──

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        const all   = await listPhotos(token);
        if (!cancelled) setItems(all);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getToken]);

  async function reload() {
    try {
      const token = await getToken();
      const all   = await listPhotos(token);
      setItems(all);
    } catch (err) {
      setError(err.message);
    }
  }

  // ── Filter URL helper ──

  function updateFilter(key, value) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!value || value === 'all') next.delete(key); else next.set(key, value);
      return next;
    }, { replace: true });
  }

  // ── Lightbox ──

  function openLightbox(id) {
    setLightboxId(id);
    window.history.pushState({ lightboxId: id }, '');
    lightboxHistoryRef.current = true;
  }

  function closeLightbox() {
    setLightboxId(null);
    if (lightboxHistoryRef.current) {
      lightboxHistoryRef.current = false;
      window.history.back();
    }
  }

  function navigateLightbox(id) {
    setLightboxId(id);
    window.history.replaceState({ lightboxId: id }, '');
  }

  useEffect(() => {
    function onPopState() {
      if (lightboxHistoryRef.current) {
        lightboxHistoryRef.current = false;
        setLightboxId(null);
      }
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ── Selection ──

  function toggleSelection(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function cancelSelection() {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setBulkError(null);
  }

  // ── Upload ──

  function handleUploadButtonClick() { fileInputRef.current?.click(); }

  function handleFilesSelected(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const entries = files.map((file, idx) => ({
      id: `${Date.now()}-${idx}`,
      file,
      preview:    null,
      exifLoaded: false,
      meta: {
        Title:    file.name.replace(/\.[^.]+$/, ''),
        DateTaken: '',
        People:   '',
        Project:  '',
        Tags:     '',
        Location: '',
        Caption:  '',
        Source:   'iPhone',
      },
    }));

    setUploadQueue(entries);
    setUploadOpen(true);
    setUploadPhase('form');
    setUploadResults({});
    e.target.value = '';

    for (const entry of entries) {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadQueue(prev => prev.map(e =>
          e.id === entry.id ? { ...e, preview: reader.result } : e
        ));
      };
      reader.readAsDataURL(entry.file);

      const objectUrl = URL.createObjectURL(entry.file);
      extractPhotoMetadata(objectUrl)
        .then(exif => {
          URL.revokeObjectURL(objectUrl);
          setUploadQueue(prev => prev.map(e => {
            if (e.id !== entry.id) return e;
            return {
              ...e,
              exifLoaded: true,
              meta: {
                ...e.meta,
                ...(exif.dateOfEvent ? { DateTaken: exif.dateOfEvent } : {}),
                ...(exif.location    ? { Location:  exif.location }    : {}),
              },
            };
          }));
        })
        .catch(() => {
          URL.revokeObjectURL(objectUrl);
          setUploadQueue(prev => prev.map(e =>
            e.id === entry.id ? { ...e, exifLoaded: true } : e
          ));
        });
    }
  }

  function handleMetaChange(fileId, field, value) {
    setUploadQueue(prev => prev.map(e =>
      e.id === fileId ? { ...e, meta: { ...e.meta, [field]: value } } : e
    ));
  }

  function handleApplyAllChange(field, value) {
    setApplyToAll(prev => ({ ...prev, [field]: value }));
  }

  function handleApplyAll() {
    setUploadQueue(prev => prev.map(entry => ({
      ...entry,
      meta: {
        ...entry.meta,
        ...(applyToAll.Project ? { Project: applyToAll.Project } : {}),
        People: [entry.meta.People, applyToAll.People].filter(Boolean).join(', '),
        Tags:   [entry.meta.Tags,   applyToAll.Tags  ].filter(Boolean).join(', '),
        ...(applyToAll.Source ? { Source: applyToAll.Source } : {}),
      },
    })));
  }

  async function handleUploadAll() {
    setUploadPhase('progress');
    const initial = {};
    for (const e of uploadQueue) initial[e.id] = { status: 'pending', error: null };
    setUploadResults(initial);

    const token = await getToken();

    await runConcurrently(uploadQueue, async (entry) => {
      setUploadResults(prev => ({ ...prev, [entry.id]: { status: 'uploading', error: null } }));
      try {
        const autoTitle = entry.file.name.replace(/\.[^.]+$/, '');
        const meta = {
          ...entry.meta,
          Title: entry.meta.Title.trim() === autoTitle ? '' : entry.meta.Title,
        };
        await uploadPhoto(token, entry.file, meta);
        setUploadResults(prev => ({ ...prev, [entry.id]: { status: 'done',  error: null } }));
      } catch (err) {
        setUploadResults(prev => ({ ...prev, [entry.id]: { status: 'error', error: err.message } }));
      }
    }, 3);

    await reload();
  }

  function handleUploadClose() {
    if (uploadPhase === 'progress') {
      const allDone = uploadQueue.every(e =>
        uploadResults[e.id]?.status === 'done' || uploadResults[e.id]?.status === 'error'
      );
      if (!allDone) return;
    }
    setUploadOpen(false);
    setUploadQueue([]);
    setApplyToAll({ Project: '', People: '', Tags: '', Source: '' });
    setUploadPhase('form');
    setUploadResults({});
  }

  // ── Bulk actions ──

  async function executeBulkAction() {
    if (!bulkModal || !bulkModal.value.trim()) return;
    const { type, value } = bulkModal;
    setBulkRunning(true);
    setBulkError(null);
    const token = await getToken();
    const ids   = [...selectedIds];
    let errorCount = 0;

    await runConcurrently(ids, async (id) => {
      const photo = (items || []).find(i => i.id === id);
      if (!photo) return;
      try {
        if (type === 'tag') {
          const tags = photo.Tags ? `${photo.Tags}, ${value.trim()}` : value.trim();
          await updatePhotoMetadata(token, id, { Tags: tags });
        } else if (type === 'album') {
          await updatePhotoMetadata(token, id, { Project: value.trim() });
        } else if (type === 'people') {
          const people = photo.People ? `${photo.People}, ${value.trim()}` : value.trim();
          await updatePhotoMetadata(token, id, { People: people });
        }
      } catch { errorCount++; }
    }, 3);

    await reload();
    setBulkModal(null);
    setBulkRunning(false);
    if (errorCount > 0) setBulkError(`${errorCount} update(s) failed.`);
  }

  async function handleBulkFavorite() {
    setBulkRunning(true);
    setBulkError(null);
    const token = await getToken();
    const ids   = [...selectedIds];
    let errorCount = 0;
    await runConcurrently(ids, async (id) => {
      try { await updatePhotoMetadata(token, id, { Favorite: true }); }
      catch { errorCount++; }
    }, 3);
    await reload();
    setBulkRunning(false);
    if (errorCount > 0) setBulkError(`${errorCount} update(s) failed.`);
  }

  async function handleBulkDownload() {
    const JSZip = (await import('jszip')).default;
    const zip   = new JSZip();
    const ids   = [...selectedIds];
    setDownloadProgress({ done: 0, total: ids.length });
    let errorCount = 0;

    await runConcurrently(ids, async (id) => {
      const photo = (items || []).find(i => i.id === id);
      if (!photo?.fullResUrl) { errorCount++; return; }
      try {
        const res = await fetch(photo.fullResUrl);
        if (!res.ok) throw new Error(String(res.status));
        zip.file(photo.name, await res.blob());
      } catch { errorCount++; }
      setDownloadProgress(p => p ? { ...p, done: p.done + 1 } : null);
    }, 3);

    const blob = await zip.generateAsync({ type: 'blob' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `photos-${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloadProgress(null);
    if (errorCount > 0) setBulkError(`${errorCount} photo(s) could not be downloaded.`);
  }

  async function handleBulkDelete() {
    setBulkRunning(true);
    setBulkError(null);
    const token = await getToken();
    const ids   = [...selectedIds];
    let errorCount = 0;
    await runConcurrently(ids, async (id) => {
      try { await deletePhoto(token, id); }
      catch { errorCount++; }
    }, 3);
    await reload();
    cancelSelection();
    setBulkDeleteConfirm(false);
    setBulkRunning(false);
    if (errorCount > 0) setBulkError(`${errorCount} photo(s) could not be moved to recycle bin.`);
  }

  // ── Filter options ──

  const albumOptions = items
    ? [...new Set(items.map(i => i.AlbumCollection).filter(Boolean))].sort()
    : [];

  const peopleOptions = items
    ? [...new Set(
        items.flatMap(i => (i.People ?? '').split(',').map(s => s.trim()).filter(Boolean))
      )].sort()
    : [];

  const tagOptions = items
    ? [...new Set(
        items.flatMap(i => (i.Tags ?? '').split(',').map(s => s.trim()).filter(Boolean))
      )].sort()
    : [];

  const yearOptions = items
    ? [...new Set(
        items.map(i => {
          if (!i.DateOfEvent) return null;
          const d = new Date(i.DateOfEvent + 'T00:00:00Z');
          return isNaN(d.getTime()) ? null : d.getUTCFullYear();
        }).filter(Boolean)
      )].sort((a, b) => b - a)
    : [];

  // Calendar-ordered months (only months that have at least one photo)
  const monthOptions = items
    ? MONTH_NAMES.filter(m =>
        items.some(i => {
          if (!i.DateOfEvent) return false;
          const d = new Date(i.DateOfEvent + 'T00:00:00Z');
          return !isNaN(d.getTime()) && MONTH_NAMES[d.getUTCMonth()] === m;
        })
      )
    : [];

  const sourceOptions = items
    ? [...new Set(items.map(i => i.Source).filter(Boolean))].sort()
    : [];

  // ── Displayed list ──

  let displayed = items ?? [];
  if (filterAlbum !== 'all')  displayed = displayed.filter(i => i.AlbumCollection === filterAlbum);
  if (filterPerson !== 'all') {
    const t = filterPerson.toLowerCase();
    displayed = displayed.filter(i =>
      (i.People ?? '').split(',').map(s => s.trim().toLowerCase()).includes(t)
    );
  }
  if (filterTag !== 'all') {
    const t = filterTag.toLowerCase();
    displayed = displayed.filter(i =>
      (i.Tags ?? '').split(',').map(s => s.trim().toLowerCase()).includes(t)
    );
  }
  if (filterYear !== 'all') {
    const y = parseInt(filterYear, 10);
    displayed = displayed.filter(i => {
      if (!i.DateOfEvent) return false;
      return new Date(i.DateOfEvent + 'T00:00:00Z').getUTCFullYear() === y;
    });
  }
  if (filterMonth !== 'all') {
    const mIdx = MONTH_NAMES.indexOf(filterMonth);
    displayed = displayed.filter(i => {
      if (!i.DateOfEvent || mIdx === -1) return false;
      return new Date(i.DateOfEvent + 'T00:00:00Z').getUTCMonth() === mIdx;
    });
  }
  if (filterSource !== 'all') {
    displayed = displayed.filter(i => i.Source === filterSource);
  }

  displayed = [...displayed].sort((a, b) => {
    if (sortBy === 'event') {
      const ad = a.DateOfEvent ?? '', bd = b.DateOfEvent ?? '';
      if (!ad && bd) return 1;
      if (ad && !bd) return -1;
      return bd.localeCompare(ad);
    }
    if (sortBy === 'recent') return (b.Created ?? '').localeCompare(a.Created ?? '');
    return (a.Title ?? '').toLowerCase().localeCompare((b.Title ?? '').toLowerCase());
  });

  const anyFilterActive = filterAlbum !== 'all' || filterPerson !== 'all' || filterTag !== 'all' ||
    filterYear !== 'all' || filterMonth !== 'all' || filterSource !== 'all';

  const secondaryFilterActive = filterYear !== 'all' || filterMonth !== 'all' || filterSource !== 'all';

  // ── Lightbox prev/next (within filtered set) ──

  const lightboxIdx   = lightboxId ? displayed.findIndex(i => i.id === lightboxId) : -1;
  const lightboxPhoto = lightboxIdx >= 0 ? displayed[lightboxIdx] : null;
  const prevPhoto     = lightboxIdx > 0 ? displayed[lightboxIdx - 1] : null;
  const nextPhoto     = lightboxIdx < displayed.length - 1 ? displayed[lightboxIdx + 1] : null;

  // ── Render ──

  return (
    <div className="FV-shelf">
      <h1 className="FV-shelf-title">Photos</h1>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFilesSelected}
      />

      {/* Toolbar / selection bar */}
      {!selectionMode ? (
        <>
          <div className="fv-browse-toolbar">
            <select className="fv-browse-select" value={sortBy} onChange={e => setSortBy(e.target.value)} aria-label="Sort by">
              <option value="event">Date of Event</option>
              <option value="recent">Recently Added</option>
              <option value="title">Title</option>
            </select>
            <select className="fv-browse-select" value={filterAlbum} onChange={e => updateFilter('album', e.target.value)} aria-label="Filter by album">
              <option value="all">All Albums</option>
              {albumOptions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select className="fv-browse-select" value={filterPerson} onChange={e => updateFilter('person', e.target.value)} aria-label="Filter by person">
              <option value="all">All People</option>
              {peopleOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select className="fv-browse-select" value={filterTag} onChange={e => updateFilter('tag', e.target.value)} aria-label="Filter by tag">
              <option value="all">All Tags</option>
              {tagOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button
              className={`fv-browse-action-btn${secondaryFilterActive ? ' fv-browse-action-btn--active' : ''}`}
              onClick={() => setShowMoreFilters(v => !v)}
              style={{ marginLeft: 'auto' }}
            >
              {showMoreFilters ? 'Fewer filters' : `More filters${secondaryFilterActive ? ' ●' : ''}`}
            </button>
            <button className="fv-browse-action-btn" onClick={() => setSelectionMode(true)}>Select</button>
            <button className="fv-browse-action-btn" onClick={handleUploadButtonClick}>+ Upload</button>
          </div>

          {/* Secondary filters */}
          {showMoreFilters && (
            <div className="fv-browse-secondary-filters">
              <select className="fv-browse-select" value={filterYear} onChange={e => updateFilter('year', e.target.value)} aria-label="Filter by year">
                <option value="all">All Years</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select className="fv-browse-select" value={filterMonth} onChange={e => updateFilter('month', e.target.value)} aria-label="Filter by month">
                <option value="all">All Months</option>
                {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select className="fv-browse-select" value={filterSource} onChange={e => updateFilter('source', e.target.value)} aria-label="Filter by source">
                <option value="all">All Sources</option>
                {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="fv-browse-checkbox-label fv-browse-checkbox-label--disabled">
                <input type="checkbox" disabled /> Has Linked Note
              </label>
              {anyFilterActive && (
                <button
                  className="fv-browse-action-btn fv-browse-action-btn--cancel"
                  onClick={() => {
                    setSearchParams({}, { replace: true });
                  }}
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="fv-browse-actions">
          <span className="fv-browse-actions-count">{selectedIds.size} selected</span>
          <button
            className="fv-browse-action-btn"
            onClick={() => setSelectedIds(new Set(displayed.map(i => i.id)))}
          >
            Select All ({displayed.length})
          </button>
          <button className="fv-browse-action-btn" disabled={selectedIds.size === 0 || bulkRunning}
            onClick={() => setBulkModal({ type: 'tag',    value: '' })}>Tag</button>
          <button className="fv-browse-action-btn" disabled={selectedIds.size === 0 || bulkRunning}
            onClick={() => setBulkModal({ type: 'album',  value: '' })}>Project</button>
          <button className="fv-browse-action-btn" disabled={selectedIds.size === 0 || bulkRunning}
            onClick={() => setBulkModal({ type: 'people', value: '' })}>People</button>
          <button className="fv-browse-action-btn" disabled={selectedIds.size === 0 || bulkRunning}
            onClick={handleBulkFavorite}>&#9733; Fav</button>
          <button className="fv-browse-action-btn" disabled={selectedIds.size === 0 || bulkRunning}
            onClick={handleBulkDownload}>
            {downloadProgress ? `${downloadProgress.done}/${downloadProgress.total}` : '\u2193 Zip'}
          </button>
          <button className="fv-browse-action-btn fv-browse-action-btn--danger"
            disabled={selectedIds.size === 0 || bulkRunning}
            onClick={() => setBulkDeleteConfirm(true)}>Delete</button>
          <button className="fv-browse-action-btn fv-browse-action-btn--cancel"
            disabled={bulkRunning} onClick={cancelSelection}>Cancel</button>
        </div>
      )}

      {bulkError && <p className="fv-browse-error">{bulkError}</p>}

      {items === null && !error && <p className="FV-empty">Loading…</p>}
      {error && <p className="fv-browse-error">{error}</p>}
      {items !== null && !error && displayed.length === 0 && (
        <p className="fv-browse-empty">
          {anyFilterActive ? 'No photos match those filters.' : 'No photos yet. Click + Upload to add some.'}
        </p>
      )}

      {displayed.length > 0 && (
        <div className="fv-browse-grid">
          {displayed.map(item => {
            const isSelected = selectedIds.has(item.id);
            const label = item.MediaType === 'Photo'
              ? photoCardLabel(item)
              : { text: item.Title, untitled: false };
            const secondary = item.MediaType !== 'Photo'
              ? (item.AlbumCollection || formatEventDate(item.DateOfEvent))
              : null;
            return (
              <div
                key={item.id}
                className={`fv-browse-card${isSelected ? ' fv-browse-card--selected' : ''}`}
                onClick={() => selectionMode ? toggleSelection(item.id) : openLightbox(item.id)}
              >
                <MediaCover item={item} aspectRatio="1:1" size="md" />
                {selectionMode && isSelected && <span className="fv-browse-card-checkmark">&#10003;</span>}
                <span
                  className={`fv-browse-card-title${label.untitled ? ' fv-browse-card-title--untitled' : ''}`}
                  title={label.text}
                >
                  {label.text}
                </span>
                {secondary && <span className="fv-browse-card-secondary">{secondary}</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <PhotoLightbox
          photo={lightboxPhoto}
          prevPhoto={prevPhoto}
          nextPhoto={nextPhoto}
          onClose={closeLightbox}
          onPrev={prevPhoto ? () => navigateLightbox(prevPhoto.id) : undefined}
          onNext={nextPhoto ? () => navigateLightbox(nextPhoto.id) : undefined}
          getToken={getToken}
        />
      )}

      {/* Upload modal */}
      {uploadOpen && (
        <UploadModal
          queue={uploadQueue}
          applyToAll={applyToAll}
          phase={uploadPhase}
          results={uploadResults}
          onClose={handleUploadClose}
          onMetaChange={handleMetaChange}
          onApplyAllChange={handleApplyAllChange}
          onApplyAll={handleApplyAll}
          onUploadAll={handleUploadAll}
        />
      )}

      {/* Bulk action modal */}
      {bulkModal && (
        <BulkModal
          modal={bulkModal}
          count={selectedIds.size}
          running={bulkRunning}
          onChange={value => setBulkModal(prev => ({ ...prev, value }))}
          onConfirm={executeBulkAction}
          onClose={() => setBulkModal(null)}
        />
      )}

      {/* Bulk delete confirmation */}
      {bulkDeleteConfirm && (
        <div className="fv-detail-confirm-overlay" onClick={() => !bulkRunning && setBulkDeleteConfirm(false)}>
          <div className="fv-detail-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete {selectedIds.size} photo{selectedIds.size !== 1 ? 's' : ''}?</h3>
            <p>These will be moved to the SharePoint recycle bin and can be recovered for up to 93 days.</p>
            <div className="fv-detail-edit-actions">
              <button className="fv-browse-action-btn fv-browse-action-btn--cancel"
                onClick={() => setBulkDeleteConfirm(false)} disabled={bulkRunning}>Cancel</button>
              <button className="fv-browse-action-btn fv-browse-action-btn--danger"
                onClick={handleBulkDelete} disabled={bulkRunning}>
                {bulkRunning ? 'Deleting…' : 'Move to Recycle Bin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
